/**
 * Sample battery and lift data for the demo (ICD v0.2 section 9, ADR 0012: two pilot vehicles carry the
 * modules). Deterministic, relative to "now", clearly fictional. As on a real device, loads come from the
 * device when it holds lift_kg_per_bar; otherwise the cloud's fallback estimate is shown as "estimated".
 */
import { estimateLoadKg, type ChargeEventRecord, type DeviceModule, type LiveSnapshot } from "@fts/core";
import { EMPTY_SETTINGS, type LiftRecord, type VehicleModuleData, type VehicleModuleSettings } from "./modules";

type Pilot = { nominalV: 48 | 80; capacityAh: number; soc: number; batA: number; batT: number; settings: VehicleModuleSettings };

/** Sample pilot devices: FL-01 (busy, 48 V, set up) and FL-03 (quiet, 80 V, not set up yet). */
export const DEMO_MODULE_DEVICES: Record<string, Pilot> = {
  "DEV-SMP-1001": {
    nominalV: 48, capacityAh: 620, soc: 62, batA: -118.5, batT: 33.5,
    settings: { batType: "lead_acid", batAh: 620, liftKgPerBar: 16, liftBarThreshold: 40, liftEmptyBar: 30, ratedCapacityKg: 2500 },
  },
  "DEV-SMP-1005": {
    nominalV: 80, capacityAh: 775, soc: 17, batA: -0.6, batT: 27.0,
    settings: { ...EMPTY_SETTINGS, ratedCapacityKg: 3000 },
  },
};

/** + charge, - discharge: a discharge current lowers the terminal voltage. */
const voltage = (p: Pilot) => {
  const cells = p.nominalV / 2;
  return Math.round((cells * (1.95 + 0.17 * (p.soc / 100)) + p.batA * 0.015 * (cells / 24)) * 10) / 10;
};

/** Adds the battery reading to the pilot devices' live states. */
export function withDemoBattery(live: LiveSnapshot[]): LiveSnapshot[] {
  return live.map((l) => {
    const p = DEMO_MODULE_DEVICES[l.deviceId];
    return p ? { ...l, soc: p.soc, batV: voltage(p), batA: p.batA, batT: p.batT, batAt: l.receivedAt ?? l.ts } : l;
  });
}

/** Tiny deterministic noise from an integer. */
const noise = (i: number) => {
  const x = Math.sin(i * 12.9898) * 43758.5453;
  return x - Math.floor(x);
};

export function demoModuleData(deviceId: string | null, now: number, settingsOverride?: VehicleModuleSettings, snapshot?: LiveSnapshot): VehicleModuleData {
  const p = deviceId ? DEMO_MODULE_DEVICES[deviceId] : undefined;
  const settings = settingsOverride ?? p?.settings ?? EMPTY_SETTINGS;
  const configured = settings.batType !== null || settings.batAh !== null || settings.liftKgPerBar !== null || settings.liftBarThreshold !== null;
  const delivery: VehicleModuleData["delivery"] = configured ? { state: "acked", at: new Date(now - 3 * 86_400_000).toISOString() } : { state: "none", at: null };
  const locked: VehicleModuleData["lockedSettings"] = configured
    ? (["batType", "batAh", "liftKgPerBar", "liftBarThreshold"] as const).filter((k) => settings[k] !== null)
    : [];
  const empty: VehicleModuleData = {
    deviceId, modules: deviceId ? [] : null, battery: null, socSeries: [], chargeEvents: [], lifts: [], liftsToday: 0, overloadsToday: 0, settings, delivery,
    lockedSettings: locked, lastDeepDischargeAt: null,
  };
  if (!p || !deviceId) return empty;
  const iso = (msAgo: number) => new Date(now - msAgo).toISOString();
  const H = 3600_000;

  // Last night: charged from 24 % to 98 % between ~10 h and ~4 h ago; since then the shift drains it.
  const chargeStartAgo = 10 * H;
  const chargeEndAgo = 4 * H;
  const kwh = (ah: number) => Math.round(ah * p.nominalV * 1.1) / 1000;
  const chargeEvents: ChargeEventRecord[] = [
    { type: "charge_start", at: iso(34 * H), soc: 21 },
    { type: "charge_end", at: iso(27.5 * H), soc: 97, ah: Math.round(0.76 * p.capacityAh), kwh: kwh(0.76 * p.capacityAh) },
    { type: "charge_start", at: iso(chargeStartAgo), soc: 24 },
    { type: "charge_end", at: iso(chargeEndAgo), soc: 98, ah: Math.round(0.74 * p.capacityAh), kwh: kwh(0.74 * p.capacityAh) },
  ];
  const socAt = (msAgo: number): number => {
    if (msAgo > chargeStartAgo) return Math.max(5, 24 + ((msAgo - chargeStartAgo) / (14 * H)) * 50);
    if (msAgo > chargeEndAgo) return 24 + ((chargeStartAgo - msAgo) / (chargeStartAgo - chargeEndAgo)) * 74;
    return p.soc + ((98 - p.soc) * msAgo) / chargeEndAgo;
  };
  const bucket = 10 * 60_000;
  const lastBucket = Math.floor(now / bucket) * bucket;
  const socSeries = Array.from({ length: 144 }, (_, i) => {
    const at = lastBucket - (143 - i) * bucket;
    return { at: new Date(at).toISOString(), soc: Math.round(Math.min(100, Math.max(0, socAt(now - at) + (noise(i) - 0.5))) * 10) / 10 };
  });

  // Lifts: one every ~7 minutes, mostly pallets, one too heavy (the device also sent an overload for it).
  const busy = deviceId === "DEV-SMP-1001";
  const rated = settings.ratedCapacityKg ?? 2500;
  const lifts: LiftRecord[] = Array.from({ length: busy ? 10 : 3 }, (_, i) => {
    const kg = i === 3 ? rated * 1.16 : 400 + Math.round(noise(i + 7) * 1600);
    const peak = Math.round((30 + kg / 16) * 10) / 10;
    const fromDevice = settings.liftKgPerBar !== null;
    const estimate = estimateLoadKg(peak, { emptyBar: settings.liftEmptyBar, kgPerBar: settings.liftKgPerBar });
    return {
      id: `demo-lift-${deviceId}-${i}`,
      at: iso((i * 7 + 2) * 60_000),
      peakBar: peak,
      maxForkMm: 600 + Math.round(noise(i + 31) * 3600),
      durMs: 5000 + Math.round(noise(i + 13) * 7000),
      loadKg: fromDevice ? Math.round((peak - 30) * settings.liftKgPerBar!) : estimate,
      estimated: !fromDevice && estimate !== null,
      overload: i === 3,
    };
  });

  const modules: DeviceModule[] = [{ type: "battery" }, { type: "lift" }];
  return {
    deviceId,
    modules,
    battery: snapshot
      ? { soc: snapshot.soc ?? null, batV: snapshot.batV ?? null, batA: snapshot.batA ?? null, batT: snapshot.batT ?? null, at: snapshot.batAt ?? null }
      : { soc: p.soc, batV: voltage(p), batA: p.batA, batT: p.batT, at: iso(5000) },
    socSeries,
    chargeEvents,
    lifts,
    liftsToday: busy ? 46 : 9,
    overloadsToday: busy ? 1 : 0,
    settings,
    delivery,
    lockedSettings: locked,
    // The quiet 80 V pilot reported a deep discharge an hour ago and has not been charged since.
    lastDeepDischargeAt: busy ? null : iso(H),
  };
}
